const HEADING_RE = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})\s*([\w+#.-]*)\s*$/;
const HR_RE = /^ {0,3}([-*_])\s*(?:\1\s*){2,}$/;
const QUOTE_RE = /^ {0,3}>\s?(.*)$/;
const ITEM_RE = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/;
const TABLE_DIVIDER_RE = /^\s*\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?\s*$/;
const CHECKBOX_RE = /^\[([ xX])\]\s+(.*)$/;

const INLINE_SOURCE = [
  "\\\\(?<escaped>[\\\\`*_{}\\[\\]()#+\\-.!>~|])",
  "|(?<tick>`+)(?<code>[\\s\\S]*?)\\k<tick>",
  "|(?<strongStar>\\*\\*)(?<strongStarBody>[\\s\\S]+?)\\*\\*",
  "|(?<strongScore>__)(?<strongScoreBody>[\\s\\S]+?)__",
  "|(?<strike>~~)(?<strikeBody>[\\s\\S]+?)~~",
  "|(?<imageAlt>!\\[([^\\]]*)\\])\\((?<imageUrl>[^)\\s]+)(?:\\s+\"[^\"]*\")?\\)",
  "|(?<linkLabel>\\[([^\\]]+)\\])\\((?<linkUrl>[^)\\s]+)(?:\\s+\"[^\"]*\")?\\)",
  "|(?<emStar>\\*)(?<emStarBody>[^*\\n]+?)\\*",
  "|(?<emScore>_)(?<emScoreBody>[^_\\n]+?)_",
  "|(?<bareUrl>https?:\\/\\/[^\\s<>()]+[^\\s<>().,;:!?])",
].join("");

const SAFE_URL_RE = /^(https?:\/\/|mailto:)/i;

const isBlockStart = (line) =>
  !line.trim() ||
  HEADING_RE.test(line) ||
  FENCE_RE.test(line) ||
  HR_RE.test(line) ||
  QUOTE_RE.test(line) ||
  ITEM_RE.test(line);

export function safeHref(url) {
  const value = String(url || "").trim();
  return SAFE_URL_RE.test(value) ? value : null;
}

export function parseInline(text) {
  const nodes = [];
  const pushText = (value) => {
    if (!value) return;
    const previous = nodes[nodes.length - 1];
    if (previous && previous.type === "text") previous.value += value;
    else nodes.push({ type: "text", value });
  };

  const regex = new RegExp(INLINE_SOURCE, "g");
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    pushText(text.slice(lastIndex, match.index));
    lastIndex = regex.lastIndex;

    const group = match.groups || {};
    if (group.escaped !== undefined) {
      pushText(group.escaped);
    } else if (group.code !== undefined) {
      nodes.push({ type: "code", value: group.code.trim() });
    } else if (group.strongStarBody !== undefined) {
      nodes.push({ type: "strong", children: parseInline(group.strongStarBody) });
    } else if (group.strongScoreBody !== undefined) {
      nodes.push({ type: "strong", children: parseInline(group.strongScoreBody) });
    } else if (group.strikeBody !== undefined) {
      nodes.push({ type: "del", children: parseInline(group.strikeBody) });
    } else if (group.imageAlt !== undefined) {
      const alt = group.imageAlt.replace(/^!\[/, "").replace(/\]$/, "");
      nodes.push({
        type: "link",
        href: safeHref(group.imageUrl),
        children: alt ? parseInline(alt) : [{ type: "text", value: group.imageUrl }],
      });
    } else if (group.linkLabel !== undefined) {
      const label = group.linkLabel.replace(/^\[|\]$/g, "");
      nodes.push({
        type: "link",
        href: safeHref(group.linkUrl),
        children: parseInline(label),
      });
    } else if (group.emStarBody !== undefined) {
      nodes.push({ type: "em", children: parseInline(group.emStarBody) });
    } else if (group.emScoreBody !== undefined) {
      nodes.push({ type: "em", children: parseInline(group.emScoreBody) });
    } else if (group.bareUrl !== undefined) {
      nodes.push({
        type: "link",
        href: safeHref(group.bareUrl),
        children: [{ type: "text", value: group.bareUrl }],
      });
    }
  }

  pushText(text.slice(lastIndex));
  return nodes;
}

function parseTable(lines, start) {
  const splitRow = (row) =>
    row
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => cell.trim());

  const head = splitRow(lines[start]).map((cell) => parseInline(cell));
  let index = start + 2;
  const rows = [];

  while (index < lines.length && lines[index].trim() && lines[index].includes("|")) {
    rows.push(splitRow(lines[index]).map((cell) => parseInline(cell)));
    index++;
  }

  return {
    block: { type: "table", head, rows },
    next: index,
  };
}

function buildListTree(entries) {
  const root = [];
  const stack = [{ indent: entries.length ? entries[0].indent : 0, items: root }];

  for (const entry of entries) {
    while (stack.length > 1 && entry.indent < stack[stack.length - 1].indent) {
      stack.pop();
    }
    const node = {
      indent: entry.indent,
      ordered: entry.ordered,
      start: entry.start,
      lines: entry.lines,
      children: [],
    };
    stack[stack.length - 1].items.push(node);
    stack.push({ indent: entry.indent + 2, items: node.children });
  }

  return root;
}

const isOrderedMarker = (marker) => /\d/.test(marker);

function nodesToBlocks(nodes) {
  const blocks = [];
  let index = 0;

  while (index < nodes.length) {
    const ordered = nodes[index].ordered;
    const group = [];
    while (index < nodes.length && nodes[index].ordered === ordered) {
      group.push(nodes[index]);
      index++;
    }
    blocks.push({
      type: "list",
      ordered,
      start: Number.isFinite(group[0].start) ? group[0].start : undefined,
      items: group.map(toListItem),
    });
  }

  return blocks;
}

function toListItem(node) {
  const blocks = parseBlocks(node.lines);
  const [head, ...tail] = blocks;
  const item = { checked: null, content: [], children: [...tail, ...nodesToBlocks(node.children)] };

  if (head && head.type === "paragraph" && head.content.length) {
    const first = head.content[0];
    if (first.type === "text") {
      const marked = first.value.match(/^\[([ xX])\]\s*/);
      if (marked) {
        item.checked = marked[1].toLowerCase() === "x";
        first.value = first.value.slice(marked[0].length);
      }
    }
    item.content = head.content;
  }

  return item;
}

function parseList(lines, start) {
  const entries = [];
  let index = start;
  const ordered = isOrderedMarker(lines[start].match(ITEM_RE)[2]);

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      const next = lines[index + 1];
      if (next && (ITEM_RE.test(next) || /^[ \t]{2,}\S/.test(next))) {
        index++;
        continue;
      }
      break;
    }

    const match = line.match(ITEM_RE);
    if (match) {
      if (isOrderedMarker(match[2]) !== ordered) break;
      const checkbox = match[3].match(CHECKBOX_RE);
      entries.push({
        indent: match[1].replace(/\t/g, "  ").length,
        ordered: isOrderedMarker(match[2]),
        start: isOrderedMarker(match[2]) ? parseInt(match[2], 10) : undefined,
        lines: [
          checkbox
            ? `${checkbox[1].toLowerCase() === "x" ? "[x] " : "[ ] "}${checkbox[2]}`
            : match[3],
        ],
      });
      index++;
      continue;
    }

    const current = entries[entries.length - 1];
    if (current && /^[ \t]+\S/.test(line)) {
      current.lines.push(line.replace(/^[ \t]{1,6}/, ""));
      index++;
      continue;
    }
    break;
  }

  const tree = buildListTree(entries);

  return {
    block: {
      type: "list",
      ordered,
      start: Number.isFinite(entries[0] && entries[0].start) ? entries[0].start : undefined,
      items: tree.map(toListItem),
    },
    next: index,
  };
}

export function parseBlocks(lines) {
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index++;
      continue;
    }

    const fence = line.match(FENCE_RE);
    if (fence) {
      const marker = fence[1][0];
      const closing = new RegExp(`^ {0,3}\\${marker}{3,}\\s*$`);
      const buffer = [];
      index++;
      while (index < lines.length && !closing.test(lines[index])) {
        buffer.push(lines[index]);
        index++;
      }
      index++;
      blocks.push({
        type: "code",
        lang: fence[2] || null,
        value: buffer.join("\n"),
      });
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ type: "hr" });
      index++;
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1].length,
        content: parseInline(heading[2].trim()),
      });
      index++;
      continue;
    }

    const quote = line.match(QUOTE_RE);
    if (quote) {
      const buffer = [];
      while (index < lines.length) {
        const current = lines[index];
        const quoted = current.match(QUOTE_RE);
        if (quoted) {
          buffer.push(quoted[1]);
          index++;
          continue;
        }
        if (!current.trim() || HR_RE.test(current) || HEADING_RE.test(current)) break;
        buffer.push(current);
        index++;
      }
      blocks.push({ type: "quote", content: parseBlocks(buffer) });
      continue;
    }

    if (ITEM_RE.test(line)) {
      const { block, next } = parseList(lines, index);
      blocks.push(block);
      index = next;
      continue;
    }

    if (
      line.includes("|") &&
      index + 1 < lines.length &&
      lines[index + 1].includes("-") &&
      TABLE_DIVIDER_RE.test(lines[index + 1])
    ) {
      const { block, next } = parseTable(lines, index);
      blocks.push(block);
      index = next;
      continue;
    }

    const buffer = [];
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index])) {
      buffer.push(lines[index]);
      index++;
    }
    if (buffer.length) {
      blocks.push({ type: "paragraph", content: parseInline(buffer.join("\n")) });
    } else {
      index++;
    }
  }

  return blocks;
}

export function parseMarkdown(markdown) {
  const lines = String(markdown == null ? "" : markdown)
    .replace(/\r\n?/g, "\n")
    .split("\n");
  return parseBlocks(lines);
}

function stripDanglingMarker(text, marker) {
  let count = 0;
  let lastIndex = -1;
  let cursor = text.indexOf(marker);

  while (cursor !== -1) {
    count++;
    lastIndex = cursor;
    cursor = text.indexOf(marker, cursor + marker.length);
  }

  if (count % 2 === 1 && lastIndex !== -1) {
    return text.slice(0, lastIndex) + text.slice(lastIndex + marker.length);
  }
  return text;
}

export function trimIncompleteMarkdown(text) {
  let out = String(text == null ? "" : text);

  ["**", "__", "~~", "*", "_", "`"].forEach((marker) => {
    out = stripDanglingMarker(out, marker);
  });

  if ((out.match(/\[/g) || []).length > (out.match(/\]/g) || []).length) {
    out = out.slice(0, out.lastIndexOf("["));
  }

  return out;
}